// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87b — on an enrollment's row in the review drawer: the prescriptions ATTACHED to it (pharmacies that
// combine same-drug Rx) and, when the pharmacy sets enrollment validity days, "Expires MM-DD-YYYY".
// Renders nothing for a pharmacy with neither — the row reads exactly as before. A combining pharmacy also gets
// the optional "Add Rx" (owner 2026-10-21; adding re-opens Clinical Review and re-checks labs).

import { useState } from 'react'
import AppButton from '../../components/ui/AppButton'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import type { TherapyRxItem, TherapyRxResponse } from '../../api/carerxCombine'
import { useDetachRx, useTherapyRx } from '../../hooks/queries/carerxCombine'
import AddRxButton from './AddRxButton'

const mute = { color: 'var(--color-text-mute)' } as const

/** The pure view (tested on its own): attached Rx + expiry, or null when there is nothing to add. */
export function TherapyRxView({ data, onDetach, busy }: {
  data: TherapyRxResponse
  onDetach?: (item: TherapyRxItem) => void
  busy?: boolean
}) {
  const attached = data.items.filter(i => i.role === 'attached')
  if (attached.length === 0 && !data.expires_on) return null
  return (
    <span data-testid={`therapy-rx-${data.therapy_id}`} style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {attached.map(a => (
        <span key={a.rx_number} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>+ Rx {a.rx_number}</span>
          <span style={mute}>attached {formatUsDate(a.attached_at)}</span>
          {onDetach && <AppButton size="small" disabled={busy} onClick={() => onDetach(a)}>Detach</AppButton>}
        </span>))}
      {data.expires_on && (
        <span style={{ fontWeight: 600, color: data.expired ? '#b91c1c' : 'var(--color-text-soft)' }}>
          {data.expired ? 'Expired' : 'Expires'} {formatUsDate(data.expires_on)}
        </span>)}
    </span>
  )
}

export default function TherapyRxList({ therapyId, onChanged }: { therapyId: string; onChanged?: () => void }) {
  const q = useTherapyRx(therapyId)
  const [target, setTarget] = useState<TherapyRxItem | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const detach = useDetachRx(() => { setTarget(null); onChanged?.() })
  const [added, setAdded] = useState<string | null>(null)
  if (q.isPending) return null   // a one-line annotation: no skeleton for a row that may stay empty
  if (q.isError) return <span role="alert" style={{ fontSize: 12, color: '#b91c1c' }}>Prescriptions unavailable — {friendlyError(q.error)}</span>
  return (
    <>
      <TherapyRxView data={q.data} busy={detach.isPending} onDetach={i => { setErr(null); setTarget(i) }} />
      {/* Owner 2026-10-21: an OPTIONAL Add Rx for a combining pharmacy; nothing is added on its own. */}
      {q.data.combine && !q.data.expired && (
        <AddRxButton therapyId={therapyId} therapyRef={q.data.therapy_ref}
          onDone={r => {
            setAdded(r.missingLabs.length > 0
              ? `Rx ${r.rxNumber} added. Back to Clinical Review; labs needed: ${r.missingLabs.join(', ')} (Lab Tracker).`
              : `Rx ${r.rxNumber} added. The enrollment is back in Clinical Review.`)
            onChanged?.()
          }} />)}
      {added && <span role="status" style={{ fontSize: 12, color: 'var(--color-primary)' }}>{added}</span>}
      <ConfirmDialog open={!!target} tone="danger" title={`Detach Rx ${target?.rx_number ?? ''} from ${q.data.therapy_ref}?`}
        confirmLabel="Detach" busyLabel="Detaching…" busy={detach.isPending} error={err}
        body={<p style={{ margin: 0 }}>The prescription leaves this enrollment and returns to the PA-approved list. Nothing in PioneerRx changes.</p>}
        requireReason={{ label: 'Why is it being detached?' }}
        onCancel={() => setTarget(null)}
        onConfirm={({ reason }) => target && detach.mutate({ therapyId, rxNumber: target.rx_number, reason },
          { onError: e => setErr(friendlyError(e)) })} />
    </>
  )
}
